import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { BookOpen, ClipboardList, User, ArrowLeft, ExternalLink, GraduationCap, Lock, CheckCircle2 } from 'lucide-react';
import { HomeUniLoader } from '../../components/HomeUniLoader';
import { toast } from 'sonner';
import { successToast } from '../../lib/successToast';

const subjectColors = [
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-[#1976D2]/10 border-[#1976D2]/30 text-[#1976D2]',
  'bg-blue-50 border-blue-200 text-[#1565C0]',
  'bg-amber-50 border-amber-200 text-amber-700',
  'bg-red-50 border-red-200 text-red-700',
  'bg-blue-50 border-blue-200 text-[#2E6CC4]',
  'bg-indigo-50 border-indigo-200 text-indigo-700',
  'bg-blue-50 border-blue-200 text-[#2E6CC4]',
  'bg-orange-50 border-orange-200 text-orange-700',
  'bg-[#1976D2]/10 border-[#1976D2]/30 text-[#1976D2]',
  'bg-[#2E6CC4]/10 border-blue-200 text-[#2E6CC4]',
  'bg-sky-50 border-sky-200 text-sky-700',
];

export default function StudentSubjects() {
  const { user } = useAuth();
  const [studentDetail, setStudentDetail] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  // Progress: map of course_id -> Set of completed lesson_ids
  const [progressMap, setProgressMap] = useState({});
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [detailRes, lesRes, asgnRes] = await Promise.all([
          api.get(`/admin/students/${user?.user_id}`),
          api.get('/lms/lessons', { params: { published: true } }),
          api.get('/assignments'),
        ]);
        setStudentDetail(detailRes.data);
        setLessons(lesRes.data);
        setAssignments(asgnRes.data);
      } catch (err) { console.error('Error:', err); }
      setLoading(false);
    };
    if (user) fetch();
  }, [user]);

  const fetchProgress = useCallback(async (courseIds) => {
    const results = await Promise.all(
      courseIds.map(cid => api.get(`/lms/courses/${cid}/progress`).catch(() => ({ data: { completed_lesson_ids: [] } })))
    );
    const map = {};
    courseIds.forEach((cid, i) => {
      map[cid] = new Set(results[i].data.completed_lesson_ids || []);
    });
    setProgressMap(prev => ({ ...prev, ...map }));
  }, []);

  const handleSubjectSelect = async (subj) => {
    setSelectedSubject(subj);
    const subjLessons = lessons.filter(l => l.subject_name === subj.name || l.subject_id === subj.subject_id || l.course_id === subj.course_id);
    const courseIds = [...new Set(subjLessons.map(l => l.course_id).filter(Boolean))];
    if (courseIds.length > 0) fetchProgress(courseIds);
  };

  const handleMarkComplete = async (lesson) => {
    setMarkingComplete(true);
    try {
      await api.post(`/lms/lessons/${lesson.lesson_id}/complete`);
      successToast('Lesson completed!');
      // Refresh progress for this lesson's course
      if (lesson.course_id) {
        const res = await api.get(`/lms/courses/${lesson.course_id}/progress`).catch(() => ({ data: { completed_lesson_ids: [] } }));
        setProgressMap(prev => ({
          ...prev,
          [lesson.course_id]: new Set(res.data.completed_lesson_ids || []),
        }));
      }
      setSelectedLesson(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setMarkingComplete(false);
  };

  if (loading) return <HomeUniLoader />;

  const mySubjects = studentDetail?.subjects || [];

  // ─── Subject Detail View ───
  if (selectedSubject) {
    const subjLessons = lessons
      .filter(l => l.subject_name === selectedSubject.name || l.subject_id === selectedSubject.subject_id || l.course_id === selectedSubject.course_id)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const subjAssignments = assignments.filter(a => a.subject_name === selectedSubject.name || a.subject_id === selectedSubject.subject_id);
    const teacherNames = [...new Set([...subjLessons.map(l => l.teacher_name), ...subjAssignments.map(a => a.teacher_name)].filter(Boolean))];

    // Determine unlock status per lesson (grouped by course_id, sorted by order)
    const isCompleted = (lesson) => progressMap[lesson.course_id]?.has(lesson.lesson_id) ?? false;
    const isUnlocked = (lesson, idx) => {
      if (idx === 0) return true;
      // Find the previous lesson in the same course
      const sameCourse = subjLessons.filter(l => l.course_id === lesson.course_id);
      const posInCourse = sameCourse.findIndex(l => l.lesson_id === lesson.lesson_id);
      if (posInCourse <= 0) return true;
      const prev = sameCourse[posInCourse - 1];
      return isCompleted(prev);
    };

    return (
      <div className="space-y-6" data-testid="subject-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelectedSubject(null)}><ArrowLeft className="w-4 h-4 mr-1" /> Back to Courses</Button>

        {/* Subject header */}
        <div className="bg-white rounded-md border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#EFF6FF] flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-[#1565C0]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1565C0]">{selectedSubject.name}</h2>
              <p className="text-xs text-slate-500">{selectedSubject.code}</p>
              {teacherNames.length > 0 && (
                <div className="flex items-center gap-1 mt-1 text-sm text-slate-600"><User className="w-4 h-4 text-[#2E6CC4]" /> {teacherNames.join(', ')}</div>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="stat-card text-center"><p className="text-2xl font-bold text-[#2E6CC4]">{subjLessons.length}</p><p className="text-xs text-slate-500">Lessons</p></div>
          <div className="stat-card text-center"><p className="text-2xl font-bold text-[#1976D2]">{subjLessons.filter(l => isCompleted(l)).length}</p><p className="text-xs text-slate-500">Completed</p></div>
          <div className="stat-card text-center"><p className="text-2xl font-bold text-amber-600">{subjAssignments.length}</p><p className="text-xs text-slate-500">Assignments</p></div>
        </div>

        {/* Lessons */}
        <Card className="rounded-md border-slate-200">
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#2E6CC4]" /> Lessons ({subjLessons.length})</CardTitle></CardHeader>
          <CardContent>
            {subjLessons.length > 0 ? (
              <div className="space-y-2">
                {subjLessons.map((l, idx) => {
                  const completed = isCompleted(l);
                  const unlocked = isUnlocked(l, idx);
                  return (
                    <div
                      key={l.lesson_id}
                      onClick={() => unlocked ? setSelectedLesson(l) : undefined}
                      className={`flex items-center gap-3 p-3 rounded-md transition-colors ${
                        unlocked
                          ? 'bg-slate-50 cursor-pointer hover:bg-[#EFF6FF]'
                          : 'bg-slate-50/50 cursor-not-allowed opacity-60'
                      }`}
                      data-testid={`lesson-row-${l.lesson_id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${completed ? 'bg-green-100' : unlocked ? 'bg-[#EFF6FF]' : 'bg-slate-100'}`}>
                        {completed ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : unlocked ? <BookOpen className="w-4 h-4 text-[#2E6CC4]" /> : <Lock className="w-4 h-4 text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${unlocked ? 'text-[#1565C0]' : 'text-slate-400'}`}>{l.title}</p>
                        <p className="text-[10px] text-slate-400">{l.lesson_type} {l.teacher_name ? `| By ${l.teacher_name}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {completed && <Badge className="bg-green-100 text-green-700 text-[10px]">Done</Badge>}
                        {!unlocked && <Badge className="bg-slate-100 text-slate-500 text-[10px]">Locked</Badge>}
                        {unlocked && !completed && <Badge variant="secondary" className="text-[10px]">{l.lesson_type}</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-4">No lessons yet for this course</p>}
          </CardContent>
        </Card>

        {/* Assignments */}
        <Card className="rounded-md border-slate-200">
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4 text-amber-600" /> Assignments ({subjAssignments.length})</CardTitle></CardHeader>
          <CardContent>
            {subjAssignments.length > 0 ? (
              <div className="space-y-2">
                {subjAssignments.map(a => {
                  const submitted = !!a.my_submission;
                  return (
                    <div key={a.assignment_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md">
                      <ClipboardList className={`w-5 h-5 flex-shrink-0 ${submitted ? 'text-[#1976D2]' : 'text-amber-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1565C0] truncate">{a.title}</p>
                        <p className="text-[10px] text-slate-400">Due: {a.due_date || 'No date'} | Max: {a.max_score} pts</p>
                      </div>
                      {submitted ? <Badge className="bg-[#1976D2]/15 text-[#1976D2]">Submitted</Badge> : <Badge className="bg-amber-100 text-amber-700">Pending</Badge>}
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-4">No assignments yet</p>}
          </CardContent>
        </Card>

        {/* Lesson viewer dialog */}
        <Dialog open={!!selectedLesson} onOpenChange={() => setSelectedLesson(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedLesson?.title}
                {selectedLesson && isCompleted(selectedLesson) && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
              </DialogTitle>
            </DialogHeader>
            {selectedLesson && (
              <div className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  {selectedLesson.subject_name && <Badge variant="secondary">{selectedLesson.subject_name}</Badge>}
                  <Badge variant="secondary">{selectedLesson.lesson_type}</Badge>
                </div>
                {selectedLesson.description && <p className="text-sm text-slate-600">{selectedLesson.description}</p>}
                {selectedLesson.content_text && (
                  <div className="bg-slate-50 rounded-md p-4 text-sm whitespace-pre-wrap">{selectedLesson.content_text}</div>
                )}
                {selectedLesson.content_url && (
                  selectedLesson.content_url.includes('youtube.com') || selectedLesson.content_url.includes('youtu.be') ? (
                    <div className="aspect-video rounded-md overflow-hidden bg-black">
                      <iframe
                        src={selectedLesson.content_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
                        className="w-full h-full"
                        allowFullScreen
                        title={selectedLesson.title}
                      />
                    </div>
                  ) : (
                    <a href={selectedLesson.content_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-[#EFF6FF] rounded-lg text-[#2E6CC4] hover:underline">
                      <ExternalLink className="w-4 h-4" /> Open resource
                    </a>
                  )
                )}
                <div className="border-t pt-4">
                  {isCompleted(selectedLesson) ? (
                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                      <CheckCircle2 className="w-5 h-5" /> Lesson completed
                    </div>
                  ) : (
                    <Button
                      className="w-full bg-[#1565C0] hover:bg-[#0D47A1] rounded-lg"
                      onClick={() => handleMarkComplete(selectedLesson)}
                      disabled={markingComplete}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {markingComplete ? 'Marking...' : 'Mark as Complete'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── Subject/Course Tiles ───
  return (
    <div className="space-y-6" data-testid="student-subjects">
      <div><h1 className="text-2xl font-bold text-[#1565C0]">My Courses</h1><p className="text-slate-500 mt-1">{mySubjects.length} courses enrolled</p></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {mySubjects.map((subj, idx) => {
          const colorClass = subjectColors[idx % subjectColors.length];
          const subjLessons = lessons.filter(l => l.subject_name === subj.name || l.subject_id === subj.subject_id || l.course_id === subj.course_id);
          const subjAssignments = assignments.filter(a => a.subject_name === subj.name || a.subject_id === subj.subject_id);
          const pendingCount = subjAssignments.filter(a => !a.my_submission).length;

          return (
            <div
              key={subj.subject_id || idx}
              onClick={() => handleSubjectSelect(subj)}
              className={`border-2 rounded-md p-5 transition-all hover:shadow-md cursor-pointer ${colorClass}`}
              data-testid={`subject-tile-${subj.subject_id}`}
            >
              <div className="w-10 h-10 rounded-lg bg-white/60 flex items-center justify-center mb-3">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold leading-tight">{subj.name}</h3>
              <p className="text-[10px] opacity-70 mt-0.5">{subj.code}</p>
              <div className="flex flex-col gap-1 mt-3">
                <div className="flex items-center gap-1.5 text-[10px]"><BookOpen className="w-3 h-3" /> {subjLessons.length} lessons</div>
                <div className="flex items-center gap-1.5 text-[10px]"><ClipboardList className="w-3 h-3" /> {subjAssignments.length} assignments</div>
                {pendingCount > 0 && <Badge className="bg-amber-200 text-amber-800 text-[9px] w-fit mt-1">{pendingCount} pending</Badge>}
              </div>
            </div>
          );
        })}
        {mySubjects.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <GraduationCap className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>No courses assigned yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
